import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import useSWR from 'swr';
import { Table, Button, Alert, Card, Modal, Spinner } from 'react-bootstrap';
import api from '../../services/api';
import { requestLaudoPresignDownload } from '../../services/laudoFiles';
import history from '../../services/history';
import FileCarousel from '../../components/laudos/FileCarousel';
import { fetchStressByCod } from '../../services/stress';
import generateStressPDF from '../laudo/stress/generateStressPDF';
import generateTransPDF from '../laudo/trans/generateTransPDF';
import generateFetalPDF from '../laudo/fetal/generateFetalPDF';
import { selectTransById } from '../../services/trans';
import { selectFetalById } from '../../services/fetal';
import selectEcoById from '../../services/ecodoppler';
import generateCongTXTPDF from '../laudo/eco/generateCongTXTPDF';
import generateDopplerPDF from '../laudo/eco/generateDopplerPDF';
import generateEcoCongenitoPDF from '../laudo/eco/generateEcoCongenitoPDF';

const fetcherAtividades = async url => {
  try {
    const { data } = await api.get(url);
    return data.filter(
      a =>
        a.CODPROCEDI !== 1 &&
        a.CODPROCEDI !== 6 &&
        a.CODPROCEDI !== 7 &&
        a.CODPROCEDI !== 58 &&
        a.CODPROCEDI !== 800 &&
        a.GUIA1 === 'SIM'
    );
  } catch (error) {
    toast.error(error?.response?.data?.error || 'Erro ao carregar atividades');
    throw error;
  }
};

const ecoConfig = {
  procedimento: 'eco',
  extensions: ['.webm', '.pdf', '.mp4'],
};

const carotidaConfig = {
  procedimento: 'carotida',
  extensions: ['.webm', '.pdf', '.mp4'],
};

const CONFIG_POR_PROCEDIMENTO = {
  3: { procedimento: 'ecg', extensions: ['.jpg', '.bmp', '.BMP'] },
  4: {
    procedimento: 'ergo',
    extensions: ['.webm', '.pdf', '.mp4', '.jpg', '.jpeg'],
  },
  11: ecoConfig,
  712: ecoConfig,
  713: ecoConfig,
  714: ecoConfig,
  12: {
    procedimento: 'trans',
    extensions: ['.jpeg', '.jpg', '.webm', '.pdf', '.mp4'],
  },
  13: { procedimento: 'stress', extensions: ['.webm', '.pdf', '.mp4'] },
  14: carotidaConfig,
  15: carotidaConfig,
  17: { procedimento: 'fetal', extensions: ['.webm', '.pdf', '.mp4'] },
};

const LAUDO_TIPO = {
  TRANS: 'trans',
  STRESS: 'stress',
  FETAL: 'fetal',
  ECO: 'eco',
};

const INITIAL_LAUDO_MODAL = {
  show: false,
  loading: false,
  tipo: null,
  titulo: '',
  data: null,
  codLaudo: null,
};

export default function Atividade() {
  const { patient } = useSelector(state => state.patient);
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [urls, setURLs] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [laudoModal, setLaudoModal] = useState(INITIAL_LAUDO_MODAL);

  const { data: atividades = [], error, isLoading } = useSWR(
    patient?.codPac ? `pacientes/${patient.codPac}/atividades` : null,
    fetcherAtividades,
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  );

  const isEcoPdf = laudoModal.tipo === LAUDO_TIPO.ECO;

  const isPdfAction =
    laudoModal.tipo === LAUDO_TIPO.TRANS ||
    laudoModal.tipo === LAUDO_TIPO.STRESS ||
    laudoModal.tipo === LAUDO_TIPO.FETAL ||
    isEcoPdf;

  const handleCloseLaudoModal = () => {
    setLaudoModal(INITIAL_LAUDO_MODAL);
  };

  const handleOpenLaudoModal = async (atividade, tipo) => {
    try {
      setLaudoModal({
        show: true,
        loading: true,
        tipo,
        titulo: atividade.DESCPROCEDI || 'Laudo',
        data: null,
        codLaudo: atividade.CODLAUDO,
      });

      let data = null;

      if (tipo === LAUDO_TIPO.TRANS) {
        const trans = await selectTransById(atividade.CODLAUDO);
        data = trans?.[0] || null;
      } else if (tipo === LAUDO_TIPO.STRESS) {
        data = await fetchStressByCod(atividade.CODLAUDO);
      } else if (tipo === LAUDO_TIPO.FETAL) {
        data = await selectFetalById(atividade.CODLAUDO);
      } else if (tipo === LAUDO_TIPO.ECO) {
        data = await selectEcoById(atividade.CODLAUDO);
      }

      if (!data) {
        toast.error('Laudo não encontrado!');
        handleCloseLaudoModal();
        return;
      }

      setLaudoModal(prev => {
        if (!prev.show) return prev;
        return {
          ...prev,
          loading: false,
          data,
        };
      });
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao carregar laudo!');
      handleCloseLaudoModal();
    }
  };

  const handleImprimirLaudo = () => {
    const { data, tipo } = laudoModal;

    if (!data || !tipo) {
      toast.error('Laudo não carregado!');
      return;
    }

    try {
      if (tipo === LAUDO_TIPO.TRANS) {
        generateTransPDF(data, 'imprimirNormal');
        return;
      }

      if (tipo === LAUDO_TIPO.STRESS) {
        generateStressPDF(data, 'imprimirNormal');
        return;
      }

      if (tipo === LAUDO_TIPO.FETAL) {
        generateFetalPDF(data, 'imprimirNormal');
        return;
      }

      if (tipo === LAUDO_TIPO.ECO) {
        if (data.TIPOECO === 'EcoC') {
          if (data.CODREALIZOU === 4860) {
            generateCongTXTPDF(data, 'imprimirTimbrado', 1);
            return;
          }

          generateEcoCongenitoPDF(data, 'imprimirTimbrado', 0.9);
          return;
        }

        generateDopplerPDF(data, 'imprimirTimbrado', 0.9);
      }
    } catch (error) {
      toast.error(error?.message || 'Erro ao abrir laudo!');
    }
  };

  function handleEcg(atividade) {
    history.push(`/reportEcg/${atividade.CODLAUDO}`);
  }

  const handleCloseFilesModal = () => {
    setShowFilesModal(false);
    setURLs([]);
    setLoadingFiles(false);
  };

  const showFiles = async atividade => {
    const config = CONFIG_POR_PROCEDIMENTO[atividade.CODPROCEDI];
    if (!config) {
      toast.warn('Laudo não permite visualização de arquivos');
      return;
    }

    try {
      setShowFilesModal(true);
      setLoadingFiles(true);
      setURLs([]);

      const { data } = await api.get('/files', {
        params: {
          codLaudo: atividade.CODLAUDO,
          procedimento: config.procedimento,
        },
      });

      const filesToView = data.filter(item =>
        config.extensions.some(ext => item.Key.includes(ext))
      );

      if (filesToView.length === 0) {
        setURLs([]);
        return;
      }

      const array = [];

      for (const file of filesToView) {
        const url = await requestLaudoPresignDownload({ key: file.Key });
        array.push({ url, Key: file.Key });
      }

      setURLs(array);
    } catch (error) {
      setURLs([]);
      toast.error(error?.response?.data?.error || 'Erro ao carregar imagens!');
    } finally {
      setLoadingFiles(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center" style={{ padding: '3rem' }}>
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger mt-2 text-center">
        <Alert.Heading>Erro ao carregar atividades!</Alert.Heading>
        <p>Tente novamente!</p>
      </Alert>
    );
  }

  return (
    <>
      <Card className="mt-2">
        <Card.Header className="font-weight-bold">Meus exames</Card.Header>
        <Card.Body>
          {atividades.length > 0 ? (
            <Table responsive="sm" bordered hover size="sm" variant="info">
              <thead>
                <tr>
                  <th>Procedimento</th>
                  <th>Convênio</th>
                  <th>Data</th>
                  <th>Laudo</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {atividades.map(a => (
                  <tr key={a.CODFATURA}>
                    {a.CODPROCEDI === 13 ? (
                      <td>
                        <Button
                          size="sm"
                          variant="link"
                          onClick={() =>
                            handleOpenLaudoModal(a, LAUDO_TIPO.STRESS)
                          }
                          aria-label={`Abrir laudo ${a.DESCPROCEDI}`}
                        >
                          {a.DESCPROCEDI}
                        </Button>
                      </td>
                    ) : a.CODPROCEDI === 14 || a.CODPROCEDI === 15 ? (
                      <td>
                        <Button
                          size="sm"
                          variant="link"
                          onClick={() => history.push(`/report-carotida/${a.CODLAUDO}`)}
                        >
                          {a.DESCPROCEDI}
                        </Button>
                      </td>
                    ) : a.CODPROCEDI === 17 ? (
                      <td>
                        <Button
                          size="sm"
                          variant="link"
                          onClick={() =>
                            handleOpenLaudoModal(a, LAUDO_TIPO.FETAL)
                          }
                          aria-label={`Abrir laudo ${a.DESCPROCEDI}`}
                        >
                          {a.DESCPROCEDI}
                        </Button>
                      </td>
                    ) : a.CODPROCEDI === 4 ? (
                      <td>
                        <Button
                          size="sm"
                          variant="link"
                          onClick={() =>
                            history.push(`/reportErgo/${a.CODLAUDO}`)
                          }
                        >
                          {a.DESCPROCEDI}
                        </Button>
                      </td>
                    ) : a.CODPROCEDI === 3 ? (
                      <td>
                        <Button
                          size="sm"
                          variant="link"
                          onClick={() => handleEcg(a)}
                        >
                          {a.DESCPROCEDI}
                        </Button>
                      </td>
                    ) : a.CODPROCEDI === 12 ? (
                      <td>
                        <Button
                          size="sm"
                          variant="link"
                          onClick={() =>
                            handleOpenLaudoModal(a, LAUDO_TIPO.TRANS)
                          }
                          aria-label={`Abrir laudo ${a.DESCPROCEDI}`}
                        >
                          {a.DESCPROCEDI}
                        </Button>
                      </td>
                    ) : a.CODPROCEDI === 11 ||
                      a.CODPROCEDI === 712 ||
                      a.CODPROCEDI === 713 ||
                      a.CODPROCEDI === 714 ? (
                      <td>
                        <Button
                          size="sm"
                          variant="link"
                          onClick={() =>
                            handleOpenLaudoModal(a, LAUDO_TIPO.ECO)
                          }
                          aria-label={`Abrir laudo ${a.DESCPROCEDI}`}
                        >
                          {a.DESCPROCEDI}
                        </Button>
                      </td>
                    ) : (
                      <td>{a.DESCPROCEDI}</td>
                    )}
                    <td>{a.SIGLACONV}</td>
                    <td>{a.dataFormatada}</td>
                    <td>{a.CODLAUDO}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => showFiles(a)}
                      >
                        Ver imagens
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <Alert variant="warning">Nenhum exame liberado</Alert>
          )}
        </Card.Body>
      </Card>

      <Modal show={showFilesModal} onHide={handleCloseFilesModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Imagens</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFiles ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
              }}
            >
              <Spinner animation="border" variant="dark" size="lg" />
            </div>
          ) : urls.length === 0 ? (
            <Alert variant="warning text-center font-italic">
              Nenhum arquivo encontrado para este laudo
            </Alert>
          ) : (
            <FileCarousel
              key={urls[0]?.Key || urls[0]?.url?.data?.preSignedUrl}
              urls={urls}
              setURLs={setURLs}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal show={laudoModal.show} onHide={handleCloseLaudoModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{laudoModal.titulo || 'Laudo'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {laudoModal.loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: 120 }}
            >
              <Spinner
                animation="border"
                role="status"
                aria-label="Carregando laudo"
              />
            </div>
          ) : (
            <Alert variant="info" className="mb-0 text-center">
              Laudo carregado. Toque em{' '}
              <strong>{isPdfAction ? 'Ver PDF' : 'Ver laudo'}</strong> para
              abrir.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseLaudoModal}
            aria-label="Fechar modal do laudo"
          >
            Fechar
          </Button>
          <Button
            variant="primary"
            onClick={handleImprimirLaudo}
            disabled={laudoModal.loading || !laudoModal.data}
            aria-label={isPdfAction ? 'Abrir PDF do laudo' : 'Abrir laudo'}
          >
            {isPdfAction ? 'Ver PDF' : 'Ver laudo'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
